import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  EventRepeat as RecurringIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";

// Category labels for constant costs
const CATEGORY_LABELS = {
  rent: "Czynsz",
  utilities: "Media",
  insurance: "Ubezpieczenie",
  software: "Oprogramowanie",
  marketing: "Marketing",
  administrative: "Administracyjne",
  other: "Inne",
};

const CostsStatistics = ({ costData, formatCurrency }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!costData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  return (
    <>
      {/* Cost Warnings */}
      {costData.warnings && costData.warnings.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          icon={<WarningIcon />}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Uwagi dotyczące kosztów:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {costData.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Profit Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Całkowity przychód
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                {formatCurrency(costData.total_revenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Costs */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalanceIcon sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Całkowite koszty
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                {formatCurrency(costData.total_costs)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                {costData.total_lessons_count} zajęć
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Income Tax */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalanceIcon sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Podatek ({costData.income_tax_rate}%)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                {formatCurrency(costData.income_tax_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Net Profit */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: costData.net_profit >= 0 
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: 'white', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Zysk netto
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                {formatCurrency(costData.net_profit)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Breakdown */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Szczegółowy podział
        </Typography>
        <Grid container spacing={3}>
          {/* Revenue Breakdown */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Przychody
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Karnety:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.pass_revenue)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Wejścia jednorazowe:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.single_entry_revenue)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Razem:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(costData.total_revenue)}</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Cost Breakdown */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Koszty
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Koszty zmienne:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Trenerzy:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_trainer_cost)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Lokalizacje:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_location_cost)}</Typography>
              </Box>
              {costData.total_variable_costs !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Suma zmiennych:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic' }}>{formatCurrency(costData.total_variable_costs)}</Typography>
                </Box>
              )}
              {costData.total_constant_costs !== undefined && costData.total_constant_costs > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Koszty stałe:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Koszty stałe:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_constant_costs)}</Typography>
                  </Box>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Razem:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{formatCurrency(costData.total_costs)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Profit Calculation */}
        <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Kalkulacja zysku
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Przychód:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_revenue)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">- Koszty:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_costs)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Zysk przed opodatkowaniem:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(costData.profit_before_tax)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">- Podatek dochodowy ({costData.income_tax_rate}%):</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.income_tax_amount)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>Zysk netto:</Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 700, 
                color: costData.net_profit >= 0 ? 'success.main' : 'error.main' 
              }}
            >
              {formatCurrency(costData.net_profit)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Trainer Costs Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} />
          Koszty trenerów ({costData.trainer_costs?.length || 0})
        </Typography>
        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Trener</TableCell>
                <TableCell align="right">Liczba zajęć</TableCell>
                <TableCell align="right">Stawka/godz.</TableCell>
                <TableCell align="right">Łączny koszt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {costData.trainer_costs && costData.trainer_costs.length > 0 ? (
                costData.trainer_costs.map((trainer, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {trainer.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{trainer.lesson_count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {trainer.hourly_cost > 0 ? formatCurrency(trainer.hourly_cost) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f093fb' }}>
                        {formatCurrency(trainer.total_cost)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Brak danych
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Location Costs Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1 }} />
          Koszty lokalizacji ({costData.location_costs?.length || 0})
        </Typography>
        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Lokalizacja</TableCell>
                <TableCell align="right">Liczba zajęć</TableCell>
                <TableCell align="right">Koszt/godz.</TableCell>
                <TableCell align="right">Łączny koszt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {costData.location_costs && costData.location_costs.length > 0 ? (
                costData.location_costs.map((location, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {location.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{location.lesson_count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {location.hourly_cost > 0 ? formatCurrency(location.hourly_cost) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f093fb' }}>
                        {formatCurrency(location.total_cost)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Brak danych
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Constant Costs Details */}
      {costData.constant_costs_breakdown && costData.total_constant_costs > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <RecurringIcon sx={{ mr: 1 }} />
            Koszty stałe ({costData.constant_costs_breakdown?.cost_details?.length || 0})
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Okres: {costData.constant_costs_breakdown.period_months?.toFixed(1)} miesięcy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
              Łącznie: {formatCurrency(costData.total_constant_costs)}
            </Typography>
          </Box>
          
          {/* By Category Summary */}
          {costData.constant_costs_breakdown.by_category && Object.keys(costData.constant_costs_breakdown.by_category).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Według kategorii:
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(costData.constant_costs_breakdown.by_category).map(([category, amount]) => (
                  <Grid item xs={6} sm={4} md={3} key={category}>
                    <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {CATEGORY_LABELS[category] || category}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(amount)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>Nazwa</TableCell>
                  <TableCell>Kategoria</TableCell>
                  <TableCell>Częstotliwość</TableCell>
                  <TableCell align="right">Kwota bazowa</TableCell>
                  <TableCell align="right">Koszt w okresie</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costData.constant_costs_breakdown.cost_details && costData.constant_costs_breakdown.cost_details.length > 0 ? (
                  costData.constant_costs_breakdown.cost_details.map((cost, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {cost.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {CATEGORY_LABELS[cost.category] || cost.category || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cost.frequency === 'monthly' ? 'Miesięcznie' : 
                           cost.frequency === 'quarterly' ? 'Kwartalnie' : 
                           cost.frequency === 'annual' ? 'Rocznie' : 
                           cost.frequency === 'one_time' ? 'Jednorazowo' : cost.frequency}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(cost.base_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                          {formatCurrency(cost.period_cost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Brak kosztów stałych
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
};

export default CostsStatistics;
